import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { LogoFull } from '@/components/Logo';
import { LanguageSelector } from '@/components/LanguageSelector';

/**
 * Unified public navigation header for all non-authenticated pages
 * (Landing, Chapters, Verses, About, Contact, etc.)
 */
export default function PublicNav() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [mobileMenu, setMobileMenu] = useState(false);

  return (
    <nav className="fixed top-0 w-full z-50 bg-white/40 backdrop-blur-2xl border-b border-white/30 px-4 sm:px-6 md:px-12 py-3 sm:py-4 shadow-sm shadow-black/[0.03]" aria-label="Main navigation">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" onClick={() => setMobileMenu(false)}>
          <LogoFull />
        </Link>
        
        {/* Desktop nav */}
        <div className="hidden sm:flex items-center gap-4">
          <LanguageSelector variant="landing" />
          {user ? (
            <Link 
              to="/dashboard" 
              className="bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity" 
              data-testid="go-to-dashboard-btn"
            >
              {t('nav.dashboard')}
            </Link>
          ) : (
            <>
              <Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors text-sm" data-testid="about-link">
                {t('landing.footer_about')}
              </Link>
              <Link to="/chapters" className="text-muted-foreground hover:text-foreground transition-colors text-sm" data-testid="explore-link">
                {t('landing.cta_explore')}
              </Link>
              <Link to="/quotes" className="text-muted-foreground hover:text-foreground transition-colors text-sm" data-testid="quotes-link">
                Gita Quotes
              </Link>
              <Link to="/contact" className="text-muted-foreground hover:text-foreground transition-colors text-sm" data-testid="contact-link">
                {t('landing.footer_contact')}
              </Link>
              <Link to="/login" className="text-muted-foreground hover:text-foreground transition-colors text-sm" data-testid="login-link">
                {t('common.login')}
              </Link>
              <Link 
                to="/signup" 
                className="bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity" 
                data-testid="get-started-btn"
              >
                {t('landing.final_cta_btn')}
              </Link>
            </>
          )}
        </div>
        
        {/* Mobile menu button */}
        <div className="sm:hidden flex items-center gap-2">
          <LanguageSelector variant="landing" />
          {user ? (
            <Link 
              to="/dashboard" 
              className="bg-primary text-primary-foreground rounded-full px-4 py-2 text-xs font-medium" 
              data-testid="go-to-dashboard-btn-mobile"
            >
              {t('nav.dashboard')}
            </Link>
          ) : (
            <button 
              onClick={() => setMobileMenu(!mobileMenu)} 
              className="p-2 text-foreground" 
              data-testid="mobile-menu-btn"
              aria-label={mobileMenu ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenu}
            >
              {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>
      
      {/* Mobile dropdown */}
      {mobileMenu && !user && (
        <div className="sm:hidden mt-3 pb-3 border-t border-border/30 pt-3 space-y-1 animate-fade-in-up" data-testid="mobile-menu">
          <Link 
            to="/about" 
            onClick={() => setMobileMenu(false)} 
            className="block px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-primary/5 rounded-lg transition-colors"
          >
            {t('landing.footer_about')}
          </Link>
          <Link 
            to="/chapters" 
            onClick={() => setMobileMenu(false)} 
            className="block px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-primary/5 rounded-lg transition-colors"
          >
            {t('landing.cta_explore')}
          </Link>
          <Link 
            to="/quotes" 
            onClick={() => setMobileMenu(false)} 
            className="block px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-primary/5 rounded-lg transition-colors"
          >
            Gita Quotes
          </Link>
          <Link 
            to="/contact" 
            onClick={() => setMobileMenu(false)} 
            className="block px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-primary/5 rounded-lg transition-colors"
          >
            {t('landing.footer_contact')}
          </Link>
          <Link 
            to="/login" 
            onClick={() => setMobileMenu(false)} 
            className="block px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-primary/5 rounded-lg transition-colors"
          >
            {t('common.login')}
          </Link>
          <Link 
            to="/signup" 
            onClick={() => setMobileMenu(false)} 
            className="block mx-3 mt-2 bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-medium text-center"
          >
            {t('landing.final_cta_btn')}
          </Link>
        </div>
      )}
    </nav>
  );
}
